import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchClinicById } from "../../store/thunks/clinicThunk";
import {
  BuildingOffice2Icon,
  EyeIcon,
  EyeSlashIcon,
} from "@heroicons/react/24/outline";
import { logInfo, logError } from "../../utils/logger";
import ClinicDetails from "./ClinicDetails";
import ClinicStaff from "./ClinicStaff";
import ClinicServices from "./ClinicServices";
import ClinicQuickbooks from "./ClinicQuickbooks";
import JourneyManager from "./JourneyManager";
import ClinicPayrollSettings from "./ClinicPayrollSettings";

// ClinicOverview component with Journey tab functionality
export default function ClinicOverview({
  selectedClinic,
  onClose,
  onUpdateClinic,
}) {
  const dispatch = useDispatch();
  const [selectedTab, setSelectedTab] = useState("details");
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [editMode, setEditMode] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  console.log("selectedClinic", selectedClinic);

  useEffect(() => {
    if (selectedClinic?._id) {
      // Only fetch if we don't have detailed clinic data or if explicitly refreshing
      const needsFetch = !selectedClinic.paymentMethods || refreshTrigger > 0;
      
      if (needsFetch) {
        console.log('[Clinic Overview] Fetching fresh clinic data for:', selectedClinic._id, { needsFetch, refreshTrigger });
        dispatch(fetchClinicById(selectedClinic._id));
      } else {
        console.log('[Clinic Overview] Clinic data already available, skipping fetch for:', selectedClinic._id);
      }
    }
  }, [selectedClinic?._id, dispatch, refreshTrigger]);

  const handleTabChange = (tab) => {
    setSelectedTab(tab);
    logInfo("Tab changed", { newTab: tab });
  };

  const handleClinicUpdate = (updatedClinic) => {
    // If we receive updated clinic data, pass it to the parent
    if (updatedClinic && onUpdateClinic) {
      onUpdateClinic(updatedClinic);
    }
    
    // Only trigger the success modal and other UI changes for the main save button
    // Not for individual field updates (which handle their own success states)
    if (!updatedClinic) {
      setRefreshTrigger((prev) => prev + 1);
      setEditMode(false);
      
      // Show success modal
      setShowSuccessModal(true);
      
      // Reset after 2 seconds
      setTimeout(() => {
        setShowSuccessModal(false);
      }, 2000);
    }
  };


  return (
    <div className="bg-white border border-gray-200 rounded-sm shadow-sm mt-4">
      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-sm shadow-xl p-6 max-w-sm w-full mx-4">
            <div className="flex items-center space-x-4">
              <div className="flex-shrink-0">
                <svg className="h-8 w-8 text-green-600" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900">Success</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Clinic {selectedClinic.name} updated successfully.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100">
        {/* Left side: Clinic Name */}
        <div className="flex items-center">
          <BuildingOffice2Icon className="h-6 w-6 text-gray-900 mr-3" />
          <div>
            <h1 className="text-xl font-semibold text-gray-900">
              {selectedClinic.name}
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              {selectedClinic.address?.street}
            </p>
            <p className="text-sm text-gray-600">
              {selectedClinic.address?.city}, {selectedClinic.address?.province}{" "}
              {selectedClinic.address?.postalCode}
            </p>
            <div className="flex items-center space-x-4 mt-1 text-sm text-gray-600">
              <span>{selectedClinic.phoneNumber}</span>
              <span>{selectedClinic.email}</span>
            </div>
          </div>
        </div>

        {/* Right side: Edit Toggle and Close Button */}
        <div className="flex items-center space-x-3">
          <button
            type="button"
            onClick={() => setEditMode(!editMode)}
            className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-sm text-gray-700 bg-white hover:bg-gray-50 transition-colors"
          >
            {editMode ? 'Cancel' : 'Edit'}
          </button>
          
          {editMode && (
            <div className="px-3 py-1 text-sm text-blue-600 bg-blue-50 rounded-sm">
              💡 Use the save button at the bottom of the form
            </div>
          )}
          
          <button
            type="button"
            onClick={() => {
              onClose();
              logInfo("Clinic overview closed", {
                clinicId: selectedClinic._id,
              });
            }}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-sm transition-colors"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="px-6 pt-4">
        <nav className="flex space-x-8 border-b border-gray-200" aria-label="Tabs">
          <button
            onClick={() => handleTabChange("details")}
            className={classNames(
              selectedTab === "details"
                ? "border-gray-900 text-gray-900"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300",
              "border-b-2 px-1 pb-3 text-sm font-medium transition-colors whitespace-nowrap"
            )}
            aria-current={selectedTab === "details" ? "page" : undefined}
          >
            Clinic Details
          </button>
          <button
            onClick={() => handleTabChange("staff")}
            className={classNames(
              selectedTab === "staff"
                ? "border-gray-900 text-gray-900"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300",
              "border-b-2 px-1 pb-3 text-sm font-medium transition-colors whitespace-nowrap"
            )}
          >
            Staff
          </button>
          <button
            onClick={() => handleTabChange("services")}
            className={classNames(
              selectedTab === "services"
                ? "border-gray-900 text-gray-900"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300",
              "border-b-2 px-1 pb-3 text-sm font-medium transition-colors whitespace-nowrap"
            )}
          >
            Services
          </button>
          <button
            onClick={() => handleTabChange("quickbooks")}
            className={classNames(
              selectedTab === "quickbooks"
                ? "border-gray-900 text-gray-900"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300",
              "border-b-2 px-1 pb-3 text-sm font-medium transition-colors whitespace-nowrap"
            )}
          >
            QuickBooks
          </button>
          <button
            onClick={() => handleTabChange("appointments")}
            className={classNames(
              selectedTab === "appointments"
                ? "border-gray-900 text-gray-900"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300",
              "border-b-2 px-1 pb-3 text-sm font-medium transition-colors whitespace-nowrap"
            )}
          >
            Appointments
          </button>
          <button
            onClick={() => handleTabChange("files")}
            className={classNames(
              selectedTab === "files"
                ? "border-gray-900 text-gray-900"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300",
              "border-b-2 px-1 pb-3 text-sm font-medium transition-colors whitespace-nowrap"
            )}
          >
            Files
          </button>
          <button
            onClick={() => handleTabChange("journeys")}
            className={classNames(
              selectedTab === "journeys"
                ? "border-gray-900 text-gray-900"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300",
              "border-b-2 px-1 pb-3 text-sm font-medium transition-colors whitespace-nowrap"
            )}
          >
            Journeys
          </button>
          <button
            onClick={() => handleTabChange("payroll")}
            className={classNames(
              selectedTab === "payroll"
                ? "border-gray-900 text-gray-900"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300",
              "border-b-2 px-1 pb-3 text-sm font-medium transition-colors whitespace-nowrap"
            )}
          >
            Payroll
          </button>
        </nav>
      </div>

      {/* Content for each tab */}
      <div className="px-6 py-4">
        {selectedTab === "details" && (
          <ClinicDetails
            clinic={selectedClinic}
            onUpdateClinic={handleClinicUpdate}
            editMode={editMode}
          />
        )}
        {selectedTab === "staff" && (
          <ClinicStaff
            clinic={selectedClinic}
            onUpdateClinic={handleClinicUpdate}
            editMode={editMode}
          />
        )}
        {selectedTab === "services" && (
          <ClinicServices
            clinic={selectedClinic}
            onUpdateClinic={handleClinicUpdate}
            editMode={editMode}
          />
        )}
        {selectedTab === "quickbooks" && (
          <ClinicQuickbooks
            clinic={selectedClinic}
            editMode={editMode}
          />
        )}
        {selectedTab === "appointments" && (
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">
              Clinic Appointments
            </h3>
            <p className="text-gray-600">
              Appointment management for {selectedClinic.name} will be displayed here.
            </p>
            <div className="bg-gray-50 rounded-sm p-4">
              <p className="text-sm text-gray-500">
                This section will contain clinic-specific appointment scheduling and management features.
              </p>
            </div>
          </div>
        )}
        {selectedTab === "files" && (
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">
              Clinic Files
            </h3>
            <p className="text-gray-600">
              File management for {selectedClinic.name} will be displayed here.
            </p>
            <div className="bg-gray-50 rounded-sm p-4">
              <p className="text-sm text-gray-500">
                This section will contain clinic-specific file storage and document management features.
              </p>
            </div>
          </div>
        )}
        {selectedTab === "journeys" && (
          <JourneyManager clinicId={selectedClinic._id} />
        )}
        {selectedTab === "payroll" && (
          <ClinicPayrollSettings
            clinic={selectedClinic}
            onUpdateClinic={handleClinicUpdate}
            editMode={editMode}
          />
        )}
      </div>
    </div>
  );
}

// Helper function for dynamic class names
function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}
